import React, { createContext, useContext, useMemo, useState } from "react";
import type { CustomerResponse } from "src/api/types/responses";

type CaseContextValue = {
  customerData: CustomerResponse | null;
  caseReference: string | null;
  setCustomerData: (data: CustomerResponse) => void;
  setAuthorisedCase: (data: CustomerResponse, reference: string) => void;
  clearCustomerData: () => void;
};

const CaseContext = createContext<CaseContextValue | undefined>(undefined);

export function CaseProvider({
  children,
  initialCustomerData = null,
  initialCaseReference = null,
}: {
  children: React.ReactNode;
  initialCustomerData?: CustomerResponse | null;
  initialCaseReference?: string | null;
}) {
  const [customerData, setCustomerDataState] = useState<CustomerResponse | null>(initialCustomerData);
  const [caseReference, setCaseReference] = useState<string | null>(initialCaseReference);

  const setCustomerData = (data: CustomerResponse) => {
    setCustomerDataState(data);
  };

  const setAuthorisedCase = (data: CustomerResponse, reference: string) => {
    setCustomerDataState(data);
    setCaseReference(reference);
  };

  const clearCustomerData = () => {
    setCustomerDataState(null);
    setCaseReference(null);
  };

  const value = useMemo(
    () => ({
      customerData,
      caseReference,
      setCustomerData,
      setAuthorisedCase,
      clearCustomerData,
    }),
    [customerData, caseReference]
  );

  return <CaseContext.Provider value={value}>{children}</CaseContext.Provider>;
}

export function useCase() {
  const context = useContext(CaseContext);

  if (!context) {
    throw new Error("useCase must be used within CaseProvider");
  }

  return context;
}
